import React, { useEffect, useRef, useMemo } from "react";
import Chart from "chart.js";

Chart.elements.Rectangle.prototype.draw = function () {
  var ctx = this._chart.ctx;
  var vm = this._view;
  var left, right, top, bottom, borderWidth;
  var cornerRadius = 8;

  if (!vm.horizontal) {
    left = vm.x - vm.width / 2;
    right = vm.x + vm.width / 2;
    top = vm.y;
    bottom = vm.base;
  } else {
    left = vm.base;
    right = vm.x;
    top = vm.y - vm.height / 2;
    bottom = vm.y + vm.height / 2;
  }

  borderWidth = vm.borderWidth;

  ctx.beginPath();
  ctx.fillStyle = vm.backgroundColor;
  ctx.strokeStyle = vm.borderColor;
  ctx.lineWidth = borderWidth;

  ctx.moveTo(left, bottom);
  ctx.lineTo(left, top + cornerRadius);
  ctx.quadraticCurveTo(left, top, left + cornerRadius, top);
  ctx.lineTo(right - cornerRadius, top);
  ctx.quadraticCurveTo(right, top, right, top + cornerRadius);
  ctx.lineTo(right, bottom);
  ctx.closePath();

  ctx.fill();
  if (borderWidth) {
    ctx.stroke();
  }
};

const lightenColor = (hex, alpha = 0.6) => {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};


const STATUS_OPTIONS = [
  { name: "Não iniciado", value: "NOT_STARTED", color: "#8b8c91" },
  { name: "Em andamento", value: "IN_PROGRESS", color: "#3b79a6" },
  { name: "Concluído", value: "COMPLETED", color: "#37753a" },
  { name: "Cancelado", value: "CANCELLED", color: "#b6423f" },
  { name: "Em espera", value: "ON_HOLD", color: "#bf7f31" },
  { name: "Em revisão", value: "UNDER_REVIEW", color: "#763081" },
];

const prepareStatusData = (records, usePercentage) => {
  if (!Array.isArray(records) || records.length === 0) return [[], [], [], [], 0];

  const counts = records.reduce((acc, { status }) => {
    const statusInfo = STATUS_OPTIONS.find(opt => opt.value === status);
    const label = statusInfo?.name || "Desconhecido";
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

  const total = Object.values(counts).reduce((sum, val) => sum + val, 0);

  const labels = Object.keys(counts);
  const data = usePercentage
    ? Object.values(counts).map(val => (val / total) * 100)
    : Object.values(counts);

  const borderColors = labels.map(label =>
    STATUS_OPTIONS.find(opt => opt.name === label)?.color || "#9e9e9e"
  );

  const fillColors = borderColors.map(color => lightenColor(color, 0.85));

  labels.push("Total");
  data.push(usePercentage ? 100 : total);
  borderColors.push("#d1d1d1");
  fillColors.push("rgba(209, 209, 209, 0.5)");

  return [labels, data, borderColors, fillColors, total];
};


const createChartConfig = (labels, data, borderColors, fillColors, usePercentage) => ({
  type: "bar",
  data: {
    labels,
    datasets: [
      {
        label: usePercentage ? "Porcentagem por Status (%)" : "Quantidade por Status",
        data,
        backgroundColor: fillColors,
        borderColor: borderColors,
        borderWidth: 2,
      },
    ],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 10, bottom: 10 } },
    legend: { display: false, position: "bottom" },
    tooltips: {
      mode: "index",
      intersect: false,
      callbacks: {
        label: (tooltipItem, chartData) => {
          const rawValue = chartData.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
          return usePercentage
            ? `Porcentagem: ${rawValue.toFixed(1)}%`
            : `Quantidade: ${rawValue}`;
        },
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            stepSize: usePercentage ? 10 : 1,
            callback: value => (usePercentage ? `${value.toFixed(1)}%` : value),
          },
          scaleLabel: {
            display: true,
            labelString: usePercentage ? "Porcentagem (%)" : "Quantidade",
          },
        },
      ],
      xAxes: [
        {
          scaleLabel: { display: true, labelString: "Status" },
          gridLines: { display: false },
        },
      ],
    },
  },
});

const StatusBarChart = ({ data, usePercentage = false }) => {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  const [labels, values, borderColors, fillColors, total] = useMemo(
    () => prepareStatusData(data, usePercentage),
    [data, usePercentage]
  );




  useEffect(() => {
    if (!canvasRef.current || labels.length === 0) return;

    if (!chartInstance.current) {
      chartInstance.current = new Chart(
        canvasRef.current,
        createChartConfig(labels, values, borderColors, fillColors, usePercentage, total)
      );
    } else {
      const chart = chartInstance.current;
      chart.data.labels = labels;
      chart.data.datasets[0].data = values;
      chart.data.datasets[0].backgroundColor = fillColors;
      chart.data.datasets[0].borderColor = borderColors;
      chart.update();
    }
  }, [labels, values, fillColors, borderColors, usePercentage, total]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  return (
    <div className="relative flex flex-col max-h-[26rem] min-w-0 bg-white w-full border rounded-lg shadow-lg">
      <div className="p-4 flex-auto">
        <div className="relative h-96">
          <canvas ref={canvasRef}></canvas>
        </div>
      </div>
    </div>
  );
};

export default StatusBarChart;
